/**
 * Ficha de una persona: cuenta, organizaciones con su plan y uso, reuniones
 * (solo metadatos), movimientos de minutos, correos, bitácora y notas internas.
 * Acciones con motivo: cortesía, minutos, volver a Gratis, suspender, borrar.
 */
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import type { AdminFichaUsuario, AdminOrgDeUsuario } from '@eco/shared'
import { errorText, useApp, useToast } from '../../components/AppContext'
import { IconArrowLeft } from '../../components/icons'
import { ErrorBox, Modal, Skeleton, StatusBadge, UsageMeter } from '../../components/ui'
import { accionLabel, compacto, fechaHora, haceCuanto, usd } from '../../lib/adminFormat'
import { formatDate, formatLength } from '../../lib/format'
import { useAsync, useDocumentTitle } from '../../lib/hooks'
import { EstadoUsuario, PLAN_LABEL, Seccion } from './common'

type Accion =
  | { tipo: 'cortesia'; org: AdminOrgDeUsuario }
  | { tipo: 'minutos'; org: AdminOrgDeUsuario }
  | { tipo: 'gratis'; org: AdminOrgDeUsuario }
  | { tipo: 'suspender' }
  | { tipo: 'reactivar' }
  | { tipo: 'borrar' }

const TIPO_MOVIMIENTO: Record<string, string> = {
  hold: 'Reserva',
  processing: 'Procesamiento',
  refund: 'Devolución',
  credit: 'Minutos regalados',
  adjust: 'Ajuste',
  summary: 'Notas'
}

const iniciales = (email: string, nombre: string | null) =>
  (nombre ?? email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')

/** Una suscripción de Paddle que sigue cobrando no se toca desde aquí. */
const cobraPaddle = (o: AdminOrgDeUsuario) =>
  o.suscripcion?.proveedor === 'paddle' &&
  ['active', 'trialing', 'past_due'].includes(o.suscripcion.estado)

export function Ficha() {
  const { id = '' } = useParams()
  const { api, me } = useApp()
  const toast = useToast()
  const nav = useNavigate()
  const f = useAsync(() => api.adminFicha(id), [api, id])
  const [accion, setAccion] = useState<Accion | null>(null)
  useDocumentTitle(f.data ? `${f.data.usuario.email} · Administración` : 'Usuario · Administración')

  if (f.error)
    return (
      <div className="stack">
        <Volver />
        <ErrorBox error={f.error} onRetry={() => void f.reload()} />
      </div>
    )
  if (!f.data) return <Skeleton h={300} />
  const d = f.data
  const u = d.usuario
  // Sobre sí mismo o sobre otro operador no se puede suspender ni borrar (la API también lo impide)
  const protegido = u.esAdmin || u.id === me?.user.id

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Volver />
      <div className="ficha-head">
        <span className="avatar" aria-hidden="true">
          {iniciales(u.email, u.nombre)}
        </span>
        <div className="grow" style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 20, overflowWrap: 'anywhere' }}>{u.email}</h2>
          <div className="row wrap" style={{ gap: 10, marginTop: 4 }}>
            <EstadoUsuario estado={d.estado} />
            {u.esAdmin && <span className="pill">Operador</span>}
            {u.nombre && <span className="muted">{u.nombre}</span>}
            <span className="faint">Desde el {formatDate(u.creadoEn)}</span>
          </div>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {u.suspendidoEn ? (
            <button className="btn" onClick={() => setAccion({ tipo: 'reactivar' })}>
              Reactivar
            </button>
          ) : (
            <button
              className="btn"
              disabled={protegido}
              title={protegido ? 'No se puede con un operador ni contigo' : undefined}
              onClick={() => setAccion({ tipo: 'suspender' })}
            >
              Suspender
            </button>
          )}
          <button
            className="btn danger"
            disabled={protegido}
            title={protegido ? 'No se puede con un operador ni contigo' : undefined}
            onClick={() => setAccion({ tipo: 'borrar' })}
          >
            Borrar cuenta
          </button>
        </div>
      </div>

      {u.suspendidoEn && (
        <div className="notice error">
          Suspendida el {fechaHora(u.suspendidoEn)}
          {u.suspendidoMotivo ? `: ${u.suspendidoMotivo}` : ''}. Solo puede descargar sus datos o
          borrar su cuenta.
        </div>
      )}

      <div className="kpis">
        <div className="kpi">
          <div className="kpi-label">Reuniones</div>
          <div className="kpi-valor">{d.totales.reuniones}</div>
          <div className="kpi-pie faint">Última: {haceCuanto(d.totales.ultimaReunion)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Horas procesadas</div>
          <div className="kpi-valor">
            {compacto(Math.round((d.totales.minutos / 60) * 10) / 10)} h
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Costo de proveedores</div>
          <div className="kpi-valor">{usd(d.totales.costoUsd)}</div>
          <div className="kpi-pie faint">De sus reuniones, desde siempre</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Términos aceptados</div>
          <div className="kpi-valor">{u.terminosVersion ? `v${u.terminosVersion}` : '—'}</div>
          <div className="kpi-pie faint">
            {u.terminosAceptadosEn ? fechaHora(u.terminosAceptadosEn) : 'Sin constancia'}
          </div>
        </div>
      </div>

      <div className="admin-grid">
        <div className="span-8 stack">
          {d.organizaciones.map((o) => (
            <Seccion
              key={o.orgId}
              titulo={o.tipo === 'personal' ? 'Cuenta personal' : `Equipo «${o.nombre}»`}
              acciones={
                <div className="row wrap" style={{ gap: 6 }}>
                  <button
                    className="btn sm"
                    disabled={cobraPaddle(o)}
                    title={cobraPaddle(o) ? 'Tiene una suscripción vigente en Paddle' : undefined}
                    onClick={() => setAccion({ tipo: 'cortesia', org: o })}
                  >
                    Dar cortesía
                  </button>
                  <button className="btn sm" onClick={() => setAccion({ tipo: 'minutos', org: o })}>
                    Regalar minutos
                  </button>
                  {o.suscripcion?.proveedor === 'manual' && (
                    <button
                      className="btn sm ghost"
                      onClick={() => setAccion({ tipo: 'gratis', org: o })}
                    >
                      Volver a Gratis
                    </button>
                  )}
                </div>
              }
            >
              <dl className="kv" style={{ margin: 0 }}>
                <dt>Plan</dt>
                <dd>
                  {PLAN_LABEL[o.uso.plan]}
                  {o.suscripcion?.proveedor === 'manual' &&
                    (o.suscripcion.periodoFin
                      ? ` · cortesía hasta el ${formatDate(o.suscripcion.periodoFin)}`
                      : ' · manual')}
                  {o.suscripcion?.proveedor === 'paddle' && ` · Paddle (${o.suscripcion.estado})`}
                  {o.suscripcion?.cancelarEn && o.suscripcion.proveedor === 'paddle'
                    ? ` · se cancela el ${formatDate(o.suscripcion.cancelarEn)}`
                    : ''}
                </dd>
                <dt>Rol</dt>
                <dd>
                  {o.rol === 'owner' ? 'Dueño' : o.rol === 'admin' ? 'Administrador' : 'Miembro'}
                  {o.tipo === 'equipo' &&
                    ` · ${o.miembros} persona${o.miembros === 1 ? '' : 's'}${
                      o.suscripcion ? ` · ${o.suscripcion.asientos} asientos` : ''
                    }`}
                </dd>
              </dl>
              <UsageMeter usage={o.uso} />
            </Seccion>
          ))}

          <Seccion titulo="Reuniones">
            {d.grabaciones.length === 0 ? (
              <p className="faint" style={{ margin: 0 }}>
                Todavía no graba ni importa reuniones.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="simple">
                  <caption className="sr">Reuniones de la persona (sin contenido)</caption>
                  <thead>
                    <tr>
                      <th scope="col">Creada</th>
                      <th scope="col">Estado</th>
                      <th scope="col">Origen</th>
                      <th scope="col" className="num">
                        Duración
                      </th>
                      <th scope="col" className="num">
                        Costo
                      </th>
                      <th scope="col">Error</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.grabaciones.map((g) => (
                      <tr key={g.id}>
                        <td>
                          {fechaHora(g.creadoEn)}
                          <div className="faint mono" style={{ fontSize: 11 }}>
                            {g.id.slice(0, 8)}
                          </div>
                        </td>
                        <td>
                          <StatusBadge estado={g.estado} />
                        </td>
                        <td>{g.origen === 'import' ? 'Importada' : (g.plataforma ?? g.modo)}</td>
                        <td className="num">{formatLength(g.duracionMs)}</td>
                        <td className="num">{usd(g.costoUsd)}</td>
                        <td className="faint">{g.errorCode ?? ''}</td>
                        <td>
                          {(g.estado === 'failed' || g.estado === 'held') && (
                            <Reintentar id={g.id} onHecho={() => void f.reload()} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="faint" style={{ margin: 0, fontSize: 12 }}>
              Por privacidad no se muestran títulos, transcripciones ni notas.
            </p>
          </Seccion>

          <Seccion titulo="Movimientos de minutos">
            {d.movimientos.length === 0 ? (
              <p className="faint" style={{ margin: 0 }}>
                Sin movimientos.
              </p>
            ) : (
              <table className="simple">
                <caption className="sr">Movimientos de minutos</caption>
                <thead>
                  <tr>
                    <th scope="col">Fecha</th>
                    <th scope="col">Tipo</th>
                    <th scope="col" className="num">
                      Minutos
                    </th>
                    <th scope="col">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {d.movimientos.map((m, i) => (
                    <tr key={i}>
                      <td>{fechaHora(m.ocurridoEn)}</td>
                      <td>{TIPO_MOVIMIENTO[m.tipo] ?? m.tipo}</td>
                      {/* Un crédito se guarda en negativo (resta del consumo): se muestra a favor */}
                      <td className="num">
                        {m.tipo === 'credit' ? `+${Math.abs(m.minutos)}` : m.minutos}
                      </td>
                      <td className="faint">{m.motivo ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>
        </div>

        <div className="span-4 stack">
          <Notas ficha={d} onGuardada={() => void f.reload()} />
          <Seccion titulo="Origen del registro">
            {u.origen ? (
              <dl className="kv" style={{ margin: 0 }}>
                {Object.entries(u.origen).map(([k, v]) => (
                  <div key={k} style={{ display: 'contents' }}>
                    <dt>{k}</dt>
                    <dd style={{ overflowWrap: 'anywhere' }}>{v}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="faint" style={{ margin: 0 }}>
                Llegó directo (sin campaña ni referente).
              </p>
            )}
          </Seccion>
          <Seccion titulo="Correos enviados">
            {d.correos.length === 0 ? (
              <p className="faint" style={{ margin: 0 }}>
                Ninguno todavía.
              </p>
            ) : (
              <ul className="timeline">
                {d.correos.map((c, i) => (
                  <li key={i}>
                    <time dateTime={c.enviadoEn}>{fechaHora(c.enviadoEn)}</time>
                    <span>{c.tipo.replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
          <Seccion titulo="Actividad">
            {d.actividad.length === 0 ? (
              <p className="faint" style={{ margin: 0 }}>
                Sin registros.
              </p>
            ) : (
              <ul className="timeline">
                {d.actividad.map((a) => (
                  <li key={a.id}>
                    <time dateTime={a.creadoEn}>{fechaHora(a.creadoEn)}</time>
                    <span>
                      {accionLabel(a.accion)}
                      {a.operadorEmail && a.operadorId !== u.id && (
                        <span className="faint"> · {a.operadorEmail}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
        </div>
      </div>

      {accion && (
        <DialogoAccion
          accion={accion}
          ficha={d}
          onClose={() => setAccion(null)}
          onHecho={(msg, salir) => {
            setAccion(null)
            toast(msg)
            if (salir) nav('/admin/usuarios')
            else void f.reload()
          }}
        />
      )}
    </div>
  )
}

function Volver() {
  return (
    <Link to="/admin/usuarios" className="back">
      <IconArrowLeft /> Usuarios
    </Link>
  )
}

function Reintentar({ id, onHecho }: { id: string; onHecho: () => void }) {
  const { api } = useApp()
  const toast = useToast()
  const [ocupado, setOcupado] = useState(false)
  return (
    <button
      className="btn sm"
      disabled={ocupado}
      onClick={async () => {
        setOcupado(true)
        try {
          await api.adminRetry(id)
          toast('Reencolada')
          onHecho()
        } catch (e) {
          toast(errorText(e), 'error')
        } finally {
          setOcupado(false)
        }
      }}
    >
      Reintentar
    </button>
  )
}

function Notas({ ficha, onGuardada }: { ficha: AdminFichaUsuario; onGuardada: () => void }) {
  const { api } = useApp()
  const toast = useToast()
  const [texto, setTexto] = useState('')
  const [ocupado, setOcupado] = useState(false)
  async function guardar() {
    if (!texto.trim()) return
    setOcupado(true)
    try {
      await api.adminNota(ficha.usuario.id, texto.trim())
      setTexto('')
      onGuardada()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setOcupado(false)
    }
  }
  return (
    <Seccion titulo="Notas internas">
      <form
        className="stack"
        style={{ gap: 8 }}
        onSubmit={(e) => {
          e.preventDefault()
          void guardar()
        }}
      >
        <textarea
          rows={3}
          maxLength={2000}
          aria-label="Nueva nota interna"
          placeholder="Solo la ven los operadores. Ej.: pidió factura a nombre de su empresa."
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <div>
          <button className="btn sm" disabled={ocupado || !texto.trim()}>
            Guardar nota
          </button>
        </div>
      </form>
      {ficha.notas.length > 0 && (
        <ul className="timeline">
          {ficha.notas.map((n) => (
            <li key={n.id}>
              <time dateTime={n.creadoEn}>{fechaHora(n.creadoEn)}</time>
              <span style={{ whiteSpace: 'pre-wrap' }}>
                {n.texto}
                {n.autorEmail && <span className="faint"> · {n.autorEmail}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Seccion>
  )
}

function DialogoAccion({
  accion,
  ficha,
  onClose,
  onHecho
}: {
  accion: Accion
  ficha: AdminFichaUsuario
  onClose: () => void
  onHecho: (mensaje: string, salir?: boolean) => void
}) {
  const { api } = useApp()
  const [motivo, setMotivo] = useState('')
  // Como texto: así el campo se puede vaciar para reescribirlo; se valida al confirmar
  const [diasTxt, setDias] = useState('14')
  const [minutosTxt, setMinutos] = useState('60')
  const dias = Number(diasTxt)
  const minutos = Number(minutosTxt)
  const diasOk = Number.isInteger(dias) && dias >= 1 && dias <= 365
  const minutosOk = Number.isInteger(minutos) && minutos >= 1 && minutos <= 100_000
  const [confirmacion, setConfirmacion] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const u = ficha.usuario

  const config = {
    cortesia: {
      titulo: 'Dar una cortesía',
      boton: 'Dar cortesía',
      peligro: false
    },
    minutos: { titulo: 'Regalar minutos', boton: 'Regalar', peligro: false },
    gratis: { titulo: 'Volver a Gratis', boton: 'Volver a Gratis', peligro: false },
    suspender: { titulo: 'Suspender la cuenta', boton: 'Suspender', peligro: true },
    reactivar: { titulo: 'Reactivar la cuenta', boton: 'Reactivar', peligro: false },
    borrar: { titulo: 'Borrar la cuenta', boton: 'Borrar para siempre', peligro: true }
  }[accion.tipo]

  const motivoValido = motivo.trim().length >= 3
  const valido =
    accion.tipo === 'gratis'
      ? true
      : accion.tipo === 'minutos'
        ? motivoValido && minutosOk
        : accion.tipo === 'borrar'
          ? motivoValido && confirmacion.trim().toLowerCase() === u.email.toLowerCase()
          : accion.tipo === 'cortesia'
            ? motivoValido && diasOk
            : motivoValido

  async function confirmar() {
    setOcupado(true)
    setError(null)
    try {
      switch (accion.tipo) {
        case 'cortesia':
          await api.adminCortesia(accion.org.orgId, dias, motivo.trim())
          return onHecho(`Cortesía de ${dias} días otorgada`)
        case 'minutos':
          await api.adminCredit(accion.org.orgId, minutos, motivo.trim())
          return onHecho(`${minutos} minutos regalados`)
        case 'gratis':
          await api.adminPlan(accion.org.orgId, 'free', null)
          return onHecho('Volvió al plan Gratis')
        case 'suspender':
          await api.adminSuspender(u.id, motivo.trim())
          return onHecho('Cuenta suspendida')
        case 'reactivar':
          await api.adminReactivar(u.id, motivo.trim())
          return onHecho('Cuenta reactivada')
        case 'borrar':
          await api.adminBorrarCuenta(u.id, confirmacion.trim(), motivo.trim())
          return onHecho('La cuenta se está borrando', true)
      }
    } catch (e) {
      setError(errorText(e))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={config.titulo}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            className={`btn ${config.peligro ? 'danger' : 'primary'}`}
            disabled={!valido || ocupado}
            onClick={() => void confirmar()}
          >
            {ocupado ? 'Un momento…' : config.boton}
          </button>
        </>
      }
    >
      <div className="stack">
        {accion.tipo === 'cortesia' && (
          <>
            <p className="muted" style={{ margin: 0 }}>
              {accion.org.tipo === 'equipo' ? 'Plan Equipo' : 'Plan Personal'} gratis para{' '}
              <strong>{accion.org.tipo === 'equipo' ? `«${accion.org.nombre}»` : u.email}</strong>.
              Al terminar vuelve sola a Gratis y le avisamos por correo.
            </p>
            <div className="row wrap" style={{ gap: 6 }}>
              {[7, 14, 30, 60].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`chip ${dias === n ? 'on' : ''}`}
                  aria-pressed={dias === n}
                  onClick={() => setDias(String(n))}
                >
                  {n} días
                </button>
              ))}
              <label className="row" style={{ gap: 6 }}>
                <span className="faint">u otros</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  step={1}
                  value={diasTxt}
                  onChange={(e) => setDias(e.target.value)}
                  style={{ width: 80 }}
                  aria-label="Días de cortesía"
                  aria-invalid={!diasOk}
                />
              </label>
            </div>
            {!diasOk && diasTxt !== '' && (
              <small className="faint">Entre 1 y 365 días, sin decimales.</small>
            )}
            {accion.org.suscripcion?.proveedor === 'manual' &&
              accion.org.suscripcion.periodoFin &&
              new Date(accion.org.suscripcion.periodoFin) > new Date() && (
                <div className="notice">
                  Ya tiene una cortesía hasta el {formatDate(accion.org.suscripcion.periodoFin)}. La
                  nueva la reemplaza: termina {diasOk ? `en ${dias} días desde hoy` : 'desde hoy'},
                  aunque eso la acorte.
                </div>
              )}
          </>
        )}
        {accion.tipo === 'minutos' && (
          <label className="field">
            <span>Minutos (se suman a este período)</span>
            <input
              type="number"
              min={1}
              max={100000}
              step={1}
              value={minutosTxt}
              onChange={(e) => setMinutos(e.target.value)}
              aria-invalid={!minutosOk}
              data-autofocus
            />
            {!minutosOk && minutosTxt !== '' && (
              <small className="faint">Entre 1 y 100.000 minutos, sin decimales.</small>
            )}
          </label>
        )}
        {accion.tipo === 'gratis' && (
          <p className="muted" style={{ margin: 0 }}>
            Termina el plan manual ahora. Sus reuniones y notas no se tocan.
          </p>
        )}
        {accion.tipo === 'suspender' && (
          <p className="muted" style={{ margin: 0 }}>
            No podrá usar eco (ni la web ni la extensión) hasta que la reactives. Podrá descargar
            sus datos y borrar su cuenta.
          </p>
        )}
        {accion.tipo === 'borrar' && (
          <>
            <div className="notice error">
              Se cancela su suscripción y se borran sus reuniones, notas y datos. No se puede
              deshacer.
            </div>
            <label className="field">
              <span>Escribe el correo de la cuenta para confirmar</span>
              <input
                value={confirmacion}
                onChange={(e) => setConfirmacion(e.target.value)}
                placeholder={u.email}
                autoComplete="off"
              />
            </label>
          </>
        )}
        {accion.tipo !== 'gratis' && (
          <label className="field">
            <span>Motivo (queda en la auditoría)</span>
            <input
              value={motivo}
              maxLength={500}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={
                accion.tipo === 'cortesia'
                  ? 'Ej.: extender la prueba para una demo con su equipo'
                  : 'Ej.: lo pidió por correo el 26 sept'
              }
              data-autofocus={accion.tipo !== 'minutos' ? true : undefined}
            />
          </label>
        )}
        {error && (
          <div className="notice error" role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  )
}
